import { DatabeatRpcClient, Auth, Event as DatabeatEvent, EventType } from './rpc/proto'

export type { Auth }

import { sha256 } from '@noble/hashes/sha256'
import { bytesToHex } from '@noble/hashes/utils'

export interface Event<K extends string = any> extends DatabeatEvent {
  event: keyof typeof EventType | K
}

// Databeat tracker options. See defaults below.
//
// Common setups:
//   a.) privacy preserving with user-tracking disabled. All user events
//       will be sent as hashed user-ids on the client-side.
//      - options: <defaultDatabeatOptions>, see below
//
//   b.) dual mode, privacy preserving + user-tracking, where privacy
//       preserving mode is used but tracks user-ids if user has permitted
//       it via `allowTracking(true)`.
//       - options: { ..., userIdentTracking: true }
//
//   c.) classic / traditional tracking mode, user must permit or
//       no events are sent.
//       - options: { ..., strictMode: true }
//
export interface DatabeatOptions {
  // defaultEnabled will enable tracking by default. This field
  // does not inform privacy preserving mode or not, see below.
  //
  // Default: true
  defaultEnabled?: boolean

  // project id related to the databeat events. This can be set
  // at the options level and always used, of passed directly to an event.
  projectId?: number

  // privacy options of the user_id field to offer privacy-preserving
  // tracking features. NOTE: there is an additional privacy-preserving
  // parameter available on the databeat-server itself called `server_user_id_salt`
  // which adds extra salt + hashes value before ingesting into the sink
  // on the server side.
  //
  // Default: all privacy options are enabled.
  privacy?: PrivacyOptions

  // userIdentTracking will enable user tracking if the user has
  // allowed it (see allowTracking() method). When disabled, the ident
  // of a user will always remain private even if allowTracking(true)
  // is called.
  //
  // Default: false
  userIdentTracking?: boolean

  // strictMode will require allowTracking(true) call before any events
  // will be sent, including privacy preserving events.
  //
  // Default: false
  strictMode?: boolean

  // flushInterval is the time in milliseconds to wait before sending
  // events to the sink server.
  //
  // Default: 500
  flushInterval?: number

  // noop will inform the tracker to skip sending events to the sink.
  // this is similar to enable/disable, but its not persisted in storage.
  // it also acts as an override and takes precedence over enable/disable.
  noop?: boolean

  // initProps is a function that returns a map of properties to be
  // set on all events. This is useful for setting properties that
  // are common to all events.
  //
  // Default: {}
  initProps?: () => {}
}

export interface PrivacyOptions {
  userIdHash?: boolean
  userAgentSalt?: boolean
  extraSalt?: string
}

export enum Ident {
  ANON = 0,
  PRIVATE = 1,
  USER = 2,
}

const MAX_QUEUE_SIZE = 150

const defaultDatabeatOptions: DatabeatOptions = {
  defaultEnabled: true,
  noop: false,
  privacy: { userIdHash: true, userAgentSalt: false },
  userIdentTracking: false,
  strictMode: false,
  flushInterval: 500,
  initProps: () => {
    return {}
  },
}

export class Databeat<K extends string> {
  private rpc: DatabeatRpcClient
  private auth: Auth
  private options: DatabeatOptions
  private enabled: boolean
  private ident: Ident
  private userId: string | null
  private sessionId: string | null
  private allowUserTracking: boolean
  private projectId: number | null
  private queue: Event<K>[]
  private flushTimeout: NodeJS.Timeout | null
  private defaultProps: {}
  private dedupeIdentKey: string | null
  private dedupeViewKey: string | null
  private ts?: number

  constructor(host: string, auth: Auth | string, options?: DatabeatOptions) {
    if (typeof auth === 'string') {
      this.auth = { jwt: auth }
    } else {
      this.auth = auth
    }
    this.rpc = new DatabeatRpcClient(host, this.auth)
    this.options = defaultDatabeatOptions
    if (options) {
      this.options = {
        defaultEnabled: options.defaultEnabled || defaultDatabeatOptions.defaultEnabled,
        projectId: options.projectId || null,
        privacy: options.privacy || defaultDatabeatOptions.privacy,
        userIdentTracking: options.userIdentTracking || defaultDatabeatOptions.userIdentTracking,
        strictMode: options.strictMode || defaultDatabeatOptions.strictMode,
        flushInterval: options.flushInterval || defaultDatabeatOptions.flushInterval,
        noop: options.noop || false,
        initProps: options.initProps || defaultDatabeatOptions.initProps,
      }
    }
    this.init()
  }

  // init is called on load by the constructor and also by reset().
  private init() {
    this.enabled = isEnabled(this.options.defaultEnabled, this.auth)
    this.projectId = this.options.projectId || null
    this.userId = null
    this.sessionId = genSessionId()
    this.queue = []
    this.flushTimeout = null
    this.defaultProps = { ...getDefaultProps(), ...this.options.initProps() }
    this.identify()
  }

  // reset will reset the user_id and session_id details. Similar to a "logout" action.
  reset(skipInit?: boolean) {
    this.enabled = isEnabled(this.options.defaultEnabled, this.auth)
    if (this.userId && !this.isAnon()) {
      this.userId = null
    }
    this.ident = Ident.ANON
    this.ts = setStorageVal({ on: this.enabled, id: this.userId, it: this.ident, ut: this.allowUserTracking })

    if (skipInit === true) {
      return
    }
    this.init()
  }

  // identify can be called to seed the userId, where the behaviour changes
  // depending on privacy or strict modes. identify is also called on init,
  // where we load the data from localStorage.
  identify(userId?: string, options?: PrivacyOptions & { allowTracking?: boolean }) {
    // skip method when used with node
    if (!isBrowser()) return

    // check if allow tracking option passed, which is a syntax sugar to calling
    // allowTracking(..) method.
    if (options && options.allowTracking !== undefined) {
      this.allowTracking(options.allowTracking)
    }

    // attempt to find userId if one isn't provided
    if (!userId) {
      const val = getStorageVal()
      if (val !== null && val.id !== null) {
        this.userId = val.id
        this.ident = val.it
        this.allowUserTracking = val.ut

        // migrate from earlier when `val.it` didn't exist
        if (val.it === undefined) {
          if (this.userId.startsWith('anon:')) {
            this.ident = Ident.ANON
          } else {
            this.ident = Ident.PRIVATE
          }
          this.ts = setStorageVal({ on: this.enabled, id: this.userId, it: this.ident, ut: this.allowUserTracking })
        } else {
          this.ident = val.it
        }
      }
    }

    // update userId or gen new one
    if (userId || this.userId === null) {
      const v = genUserId(userId, this.allowUserTracking, this.options)
      this.userId = v.userId
      this.ident = v.ident
      this.ts = setStorageVal({ on: this.enabled, id: this.userId, it: this.ident, ut: this.allowUserTracking })
    }

    // simple dedupe of consecutive calls to identify()
    const dedupeIdentKey = `${this.userId}:${this.sessionId}`
    if (this.dedupeIdentKey === dedupeIdentKey) return
    this.dedupeIdentKey = dedupeIdentKey


    // track init and flush it right away
    this.track({ event: 'INIT', source: getPagePath(), props: this.defaultProps })

    // flush queued events
    // NOTE: we do not call this.trackView() at this point, as we expect the webapp to do so.
    this.flush()
  }

  // allowTracking is used by strict mode to enable/disable tracking.
  allowTracking(allowTracking: boolean) {
    this.allowUserTracking = allowTracking
    setStorageVal({ on: this.enabled, id: this.userId, it: this.ident, ut: this.allowUserTracking })

    // in strict mode, we enable/disable tracking
    if (this.options.strictMode) {
      if (allowTracking) {
        this.enable()
      } else {
        this.disable()
      }
    }
  }

  async track(events: Event<K> | Event<K>[], options?: { flush?: boolean }) {
    if (isBrowser() && this.userId === null) {
      throw new Error('databeat: init first')
    }

    if (Array.isArray(events)) {
      for (let i = 0; i < events.length; i++) {
        events[i].ident = this.ident
        events[i].userId = this.userId
        events[i].sessionId = this.sessionId
        if (!events[i].projectId && this.projectId) {
          events[i].projectId = this.projectId
        }
      }
      this.queue.push(...events)
    } else {
      events.ident = this.ident
      events.userId = this.userId
      events.sessionId = this.sessionId
      if (!events.projectId && this.projectId) {
        events.projectId = this.projectId
      }
      this.queue.push(events)
    }

    // Limit the queue size
    if (this.queue.length > MAX_QUEUE_SIZE) {
      this.queue = this.queue.splice(this.queue.length - MAX_QUEUE_SIZE)
    }

    // flush
    if (!this.enabled) return
    if (options && options.flush) {
      // flush events right away
      await this.flush()
    } else if (this.flushTimeout === null) {
      // flush events in the future (which will auto-batch)
      this.flushTimeout = setTimeout(() => {
        this.flush()
      }, this.options.flushInterval)
    }
  }

  trackView(props: { [key: string]: string } = {}) {
    const eventProps = { ...this.defaultProps, ...props }
    const eventSource = getPagePath()

    // simple dedupe -- skip if we're already on this page
    // NOTE: even though we dedupe in the flush() method, we do it here in case of timing
    // between flushes on the same page.
    const pageKey = `${eventSource}:${JSON.stringify(eventProps)}:${this.userId}:${this.sessionId}`
    if (this.dedupeViewKey === pageKey) return
    this.dedupeViewKey = pageKey

    // track the page view
    this.track({ event: 'VIEW', source: eventSource, props: eventProps })
  }

  flush = async () => {
    if (this.flushTimeout !== null) {
      clearTimeout(this.flushTimeout)
      this.flushTimeout = null
    }

    if (!this.enabled) return
    if (this.options.noop) return
    if (this.options.strictMode && !this.allowUserTracking) return
    if (this.queue.length === 0) return

    // dedupe the queue
    const events = this.dedupedQueue()
    this.queue = []

    // send the events
    try {
      await this.rpc.tick({ events: events })
    } catch (err) {
      console.warn(`databeat flush failed: ${err}`)
    }
  }

  isEnabled() {
    return this.enabled
  }

  isAnon() {
    return this.ident === Ident.ANON
  }

  getIdent() {
    return this.ident
  }

  getUserId() {
    return this.userId
  }

  getSessionId() {
    return this.sessionId
  }

  getStorageTS(): number | undefined {
    if (this.ts) {
      return this.ts
    }
    const val = getStorageVal()
    if (val === undefined || val === null || val.id === null) {
      return undefined
    }
    this.ts = val.ts
    return this.ts
  }

  enable() {
    if (!isAuthEnabled(this.auth)) {
      this.enabled = false
      return
    }
    if (!isBrowser()) return
    this.enabled = true
    this.ts = setStorageVal({ on: this.enabled, id: this.userId, it: this.ident, ut: this.allowUserTracking })
    this.identify()
  }

  disable() {
    if (!isBrowser()) return
    this.enabled = false
    this.ts = setStorageVal({ on: this.enabled, id: this.userId, it: this.ident, ut: this.allowUserTracking })
  }

  private dedupedQueue = () => {
    const uniqEvents = new Map()
    for (let i = 0; i < this.queue.length; i++) {
      const ev = this.queue[i]
      const key = `${ev.event}:${ev.source}:${ev.userId}:${ev.sessionId}:${JSON.stringify(ev.props)}:${JSON.stringify(ev.nums)}`
      if (!uniqEvents.has(key)) {
        uniqEvents.set(key, ev)
      }
    }
    return Array.from(uniqEvents.values())
  }
}

export const isBrowser = () => {
  return (
    typeof window === 'object' &&
    typeof window.localStorage === 'object' &&
    typeof window.localStorage.getItem === 'function' &&
    typeof window.localStorage.setItem === 'function'
  )
}

const isEnabled = (defaultEnabled?: boolean, auth?: Auth) => {
  if (!isAuthEnabled(auth)) {
    return false
  }
  if (!isBrowser()) {
    return true
  }
  const val = getStorageVal()
  if (val === null) {
    if (defaultEnabled !== null && defaultEnabled !== undefined) {
      return defaultEnabled
    } else {
      return true
    }
  } else {
    return val.on
  }
}

const isAuthEnabled = (auth?: Auth) => {
  if (auth?.jwt?.length > 0) {
    return true
  }
  if (auth?.headers && Object.keys(auth.headers).length > 0) {
    return true
  }
  return false
}

const getPagePath = () => {
  try {
    return window.location.pathname
  } catch (err) {
    return ''
  }
}

const getDefaultProps = () => {
  if (!isBrowser()) return {}

  const params = new URL(window.location.href).searchParams
  const props = {}

  props['origin'] = window.location.origin

  if (params.get('ref') && params.get('ref') !== '') {
    props['ref'] = params.get('ref') || ''
  }
  if (params.get('source') && params.get('source') !== '') {
    props['source'] = params.get('source') || ''
  }
  if (params.get('utm_source') && params.get('utm_source') !== '') {
    props['utmSource'] = params.get('utm_source') || ''
  }
  if (params.get('utm_campaign') && params.get('utm_campaign') !== '') {
    props['utmCampaign'] = params.get('utm_campaign') || ''
  }
  if (params.get('utm_medium') && params.get('utm_medium') !== '') {
    props['utmMedium'] = params.get('utm_medium') || ''
  }
  if (params.get('utm_content') && params.get('utm_content') !== '') {
    props['utmContent'] = params.get('utm_content') || ''
  }
  if (params.get('utm_term') && params.get('utm_term') !== '') {
    props['utmTerm'] = params.get('utm_term') || ''
  }
  if (document.referrer && document.referrer !== '') {
    props['referrer'] = document.referrer
  }
  if (params.get('gclid') && params.get('gclid') !== '') {
    props['gclid'] = params.get('gclid') || ''
  }
  if (params.get('wbraid') && params.get('wbraid') !== '') {
    props['wbraid'] = params.get('wbraid') || ''
  }

  return props
}

const storageKey = '_dbeat'

interface StorageVal {
  on: boolean
  id: string | null
  it: Ident
  ut: boolean | null
  ts?: number
}

export const getStorageVal = (): StorageVal | null => {
  try {
    return JSON.parse(window.localStorage.getItem(storageKey))
  } catch (err) {
    return null
  }
}

const setStorageVal = (val: StorageVal): number | undefined => {
  try {
    if (!val.ts) {
      val.ts = Math.floor(Date.now() / 1000)
    }
    window.localStorage.setItem(storageKey, JSON.stringify(val))
    return val.ts
  } catch (err) {
    return
  }
}

export const genUserId = (seed: string, allowUserTracking: boolean, options: DatabeatOptions) => {
  // Generate user id based on random number
  if (!seed || seed === null || seed === '') {
    const id = Math.floor(Math.random() * 100000000000000)
    return { userId: `${id}`, ident: Ident.ANON }
  }

  // in `userIdentTracking` mode when the user has allowed tracking,
  // we can pass the user id as is, with ident of USER.
  if (options.userIdentTracking && allowUserTracking) {
    return { userId: seed, ident: Ident.USER }
  }

  // Generate user id based on hash of seed + optional extra salt
  if (options.privacy.userIdHash === true || !options.userIdentTracking || !allowUserTracking) {
    if (isBrowser() && options.privacy.userAgentSalt === true) {
      seed = seed + ':' + navigator.userAgent
    }
    if (options.privacy.extraSalt && options.privacy.extraSalt.length > 0) {
      seed = seed + ':' + options.privacy.extraSalt
    }
    return { userId: bytesToHex(sha256(seed)).substring(0, 50), ident: Ident.PRIVATE }
  } else {
    throw new Error('invalid configuration')
  }
}

export const genSessionId = () => {
  const isSessionStorageAvail =
    isBrowser() &&
    typeof window.sessionStorage === 'object' &&
    typeof window.sessionStorage.getItem === 'function' &&
    typeof window.sessionStorage.setItem === 'function'

  if (!isSessionStorageAvail) {
    return genRandHex(16)
  } else {
    const sid = window.sessionStorage.getItem(storageKey)
    if (!!sid && sid.length > 0) {
      return sid
    } else {
      const sid = genRandHex(16)
      window.sessionStorage.setItem(storageKey, sid)
      return sid
    }
  }
}

const genRandHex = (n: number) => {
  let hexString = ''
  for (let i = 0; i < n; i++) {
    hexString += Math.floor(Math.random() * 16).toString(16)
  }
  return '0x' + hexString
}

if (typeof process === 'object' && process.version && process.version < 'v18') {
  console.error(`ERROR! expecting node v18+ but your node version is reporting ${process.version}`)
  if (process && process.exit) {
    process.exit(1)
  }
}
